/**
 * `useTopUpWatch` — drives the "your top-up is landing" state on the family
 * balance page.
 *
 * A Stripe top-up credits the wallet only when the `payment_intent.succeeded`
 * webhook lands (`server/src/managers/stripe-wallet.ts`), which arrives a
 * beat after the browser confirms the payment. Firing a single refetch on
 * confirm races that webhook and usually loses, leaving the balance card
 * showing the OLD number until a hard refresh — the confusion Sheila flagged
 * (QA 7/21, item 9) that invites double top-ups. This hook instead polls
 * `/api/billing/family` on a bounded backoff until the new balance appears,
 * surfacing a live "Updating…" state in the meantime and a gentle
 * "still processing" note if the webhook is unusually slow.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { BillingFamilyResponse } from '@/api/billing-family'
import { buildTopUpPollDelays, readAccountBalanceFromBillingCache, topUpHasLanded } from '@/lib/top-up-polling'

/** Prefix key — matches every `first=N` variant of the billing family query. */
const BILLING_FAMILY_KEY = ['billing', 'family'] as const

export interface TopUpWatchState {
  /** Account whose Stripe credit we're waiting on, or null when idle. */
  pendingAccountId: string | null
  /**
   * True once the poll window (~60s) elapsed without the credit landing — the
   * card shows a gentle "still processing" note instead of a live spinner.
   */
  stillProcessing: boolean
}

export interface UseTopUpWatchResult extends TopUpWatchState {
  /** Begin watching `accountId` for an `amountCents` credit to land. */
  watch: (args: { accountId: string; amountCents: number }) => void
}

export function useTopUpWatch(): UseTopUpWatchResult {
  const qc = useQueryClient()
  const [state, setState] = useState<TopUpWatchState>({ pendingAccountId: null, stillProcessing: false })
  // Bumped on every new watch (and on unmount) so a superseded or orphaned
  // poll loop cancels itself instead of racing the current one.
  const runRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // Cancel any in-flight poll loop: bump the run token (so a tick mid-await
  // bails instead of calling setState) and clear the pending timer. Wrapped in
  // a stable callback so the ref access stays out of the effect cleanup body.
  const cancelActiveWatch = useCallback(() => {
    runRef.current++
    clearTimer()
  }, [clearTimer])

  const readBalance = useCallback(
    (accountId: string): number | null => {
      const caches = qc.getQueriesData<BillingFamilyResponse>({ queryKey: BILLING_FAMILY_KEY }).map(([, data]) => data)
      return readAccountBalanceFromBillingCache(caches, accountId)
    },
    [qc],
  )

  const watch = useCallback(
    ({ accountId, amountCents }: { accountId: string; amountCents: number }) => {
      clearTimer()
      const run = ++runRef.current
      // Baseline off the balance the page is already showing; the credit has
      // landed once we observe at least baseline + this top-up.
      const target = (readBalance(accountId) ?? 0) + amountCents
      const delays = buildTopUpPollDelays()
      setState({ pendingAccountId: accountId, stillProcessing: false })

      let index = 0
      const tick = async () => {
        if (run !== runRef.current) {
          return
        }
        await qc.refetchQueries({ queryKey: BILLING_FAMILY_KEY })
        if (run !== runRef.current) {
          return
        }
        const balance = readBalance(accountId)
        if (balance !== null && topUpHasLanded(balance, target)) {
          setState({ pendingAccountId: null, stillProcessing: false })
          return
        }
        if (index >= delays.length) {
          // Budget exhausted — stop polling and leave a gentle note. The real
          // value still refreshes on the next page load / query invalidation.
          setState({ pendingAccountId: accountId, stillProcessing: true })
          return
        }
        timerRef.current = setTimeout(() => void tick(), delays[index++])
      }

      timerRef.current = setTimeout(() => void tick(), delays[index++])
    },
    [clearTimer, qc, readBalance],
  )

  useEffect(() => {
    return cancelActiveWatch
  }, [cancelActiveWatch])

  return { ...state, watch }
}
